import React from 'react'
import { Route } from 'react-router-dom'
import { ConnectedSwitch } from 'reactRouterConnected'
import Loadable from 'react-loadable'
import Page from 'components/LayoutComponents/Page'
import NotFoundPage from 'pages/DefaultPages/NotFoundPage'
import HomePage from 'pages/DefaultPages/HomePage'

const loadable = loader =>
  Loadable({
    loader,
    delay: false,
    loading: () => null,
  })

const loadableRoutes = {
  // Default Pages
  '/login': {
    component: loadable(() => import('pages/DefaultPages/LoginPage')),
  },
  '/empty': {
    component: loadable(() => import('pages/DefaultPages/EmptyPage')),
  },
  '/rental/orders': {
    component: loadable(() => import('pages/Apps/Ecommerce/RentalOrderspage')),
  },
  '/rental/order/:id': {
    component: loadable(() => import('pages/Apps/Ecommerce/RentalOrderDetail')),
  },
  '/rental/overdueorders': {
    component: loadable(() => import('pages/Apps/Ecommerce/RentalOverDuepage')),
  },
  '/customers': {
    component: loadable(() => import('pages/Apps/Ecommerce/CustomerListspage')),
  },
  '/customer/:id': {
    component: loadable(() => import('pages/Apps/Ecommerce/CustomerDetailpage')),
  },
  '/devicebuyorders': {
    component: loadable(() => import('pages/Apps/Ecommerce/DeviceBuyOrder')),
  },
  '/device-recharge-orders': {
    component: loadable(() => import('pages/Apps/Ecommerce/DeviceRechargeOrder')),
  },
  '/recharge/device/order/:id': {
    component: loadable(() => import('pages/Apps/Ecommerce/DeviceRechargeOrderDetailPage')),
  },
  '/device/order/:id': {
    component: loadable(() => import('pages/Apps/Ecommerce/DeviceOrderDetailPage')),
  },
  '/simbuyorders': {
    component: loadable(() => import('pages/Apps/Ecommerce/SimCardBuyOrderPage')),
  },
  '/sim/order/:id': {
    component: loadable(() => import('pages/Apps/Ecommerce/SimOrderDetailPage')),
  },
  '/recharge/order/:id': {
    component: loadable(() => import('pages/Apps/Ecommerce/RechargeOrderDetailsPage')),
  },
  '/api-recharge-orders': {
    component: loadable(() => import('pages/Apps/Ecommerce/ApiRechargeOrder')),
  },
  /*'/recharge/api/order/:id': {
    component: loadable(() => import('pages/Apps/Ecommerce/ApiRechargeOrderDetailPage')),
  },*/
  '/autorenewals': {
    component: loadable(() => import('pages/Apps/Ecommerce/AutoRenewalPage')),
  },
  '/autorenewal/profile/:id': {
    component: loadable(() => import('pages/Apps/Ecommerce/AutoRenewalDetailPage')),
  },
  '/products': {
    component: loadable(() => import('pages/Apps/Ecommerce/ManageProductsPage')),
  },
  '/admins': {
    component: loadable(() => import('pages/Apps/Ecommerce/AdminListingPage')),
  },
  '/add/user': {
    component: loadable(() => import('pages/Apps/Ecommerce/AddUserPage')),
  },
  '/devices/inventory': {
    component: loadable(() => import('pages/Apps/Ecommerce/Inventory/Devices')),
  },
  '/sim/inventory': {
    component: loadable(() => import('pages/Apps/Ecommerce/Inventory/Simcards')),
  },
  '/activelines/inventory': {
    component: loadable(() => import('pages/Apps/Ecommerce/Inventory/ActiveLines')),
  },
  '/expiration/devices': {
    component: loadable(() =>
      import('pages/Apps/Ecommerce/InventoryManagement/Expiration/Devices'),
    ),
  },
  '/expiration/sim': {
    component: loadable(() =>
      import('pages/Apps/Ecommerce/InventoryManagement/Expiration/SimCard'),
    ),
  },
  '/management/devices': {
    component: loadable(() => import('pages/Apps/Ecommerce/InventoryManagement/Devices')),
  },
  '/management/lines': {
    component: loadable(() => import('pages/Apps/Ecommerce/InventoryManagement/Lines')),
  },
  '/recharge/orders': {
    component: loadable(() => import('pages/Apps/Ecommerce/RechargeOrdersPage')),
  },
  '/activate/amazonorders': {
    component: loadable(() => import('pages/Apps/Ecommerce/AmazonOrder')),
  },
  '/activate/devicerequests': {
    component: loadable(() => import('pages/Apps/Ecommerce/ActivateDeviceRequests')),
  },
  '/activate/simrequests': {
    component: loadable(() => import('pages/Apps/Ecommerce/ActivateSimRequests')),
  },
  '/categories': {
    component: loadable(() => import('pages/Apps/Ecommerce/Catalog/Categories/ListCategories')),
  },
  '/options': {
    component: loadable(() => import('pages/Apps/Ecommerce/Catalog/Options/ListOptions')),
  },
  '/add/option': {
    component: loadable(() => import('pages/Apps/Ecommerce/Catalog/Options/AddOption')),
  },
  '/edit/option': {
    component: loadable(() => import('pages/Apps/Ecommerce/Catalog/Options/EditOption')),
  },
  '/recurring/profiles': {
    component: loadable(() =>
      import('pages/Apps/Ecommerce/Catalog/RecurringProfile/ListRecurringProfile'),
    ),
  },
  '/add/recurring/profile': {
    component: loadable(() =>
      import('pages/Apps/Ecommerce/Catalog/RecurringProfile/AddRecurringProfile'),
    ),
  },
  '/edit/recurring/profile': {
    component: loadable(() =>
      import('pages/Apps/Ecommerce/Catalog/RecurringProfile/EditRecurringProfile'),
    ),
  },
  '/list/products': {
    component: loadable(() => import('pages/Apps/Ecommerce/Catalog/Products/ListProducts')),
  },
  '/add/category': {
    component: loadable(() => import('pages/Apps/Ecommerce/Catalog/Categories/AddCategory')),
  },
  '/add/product': {
    component: loadable(() => import('pages/Apps/Ecommerce/Catalog/Products/AddProduct')),
  },
  '/discounts': {
    component: loadable(() => import('pages/Apps/Ecommerce/Promotion/Discounts/List')),
  },
  '/add/discount': {
    component: loadable(() => import('pages/Apps/Ecommerce/Promotion/Discounts/Add')),
  },
  '/edit/discount': {
    component: loadable(() => import('pages/Apps/Ecommerce/Promotion/Discounts/Edit')),
  },
  '/edit/product': {
    component: loadable(() => import('pages/Apps/Ecommerce/Catalog/Products/EditProduct')),
  },
  '/edit/category': {
    component: loadable(() => import('pages/Apps/Ecommerce/Catalog/Categories/EditCategory')),
  },

  // Dashboards
  '/dashboard/alpha': {
    component: loadable(() => import('pages/Dashboard/DashboardAlphaPage')),
  },
}

class Routes extends React.Component {
  timeoutId = null

  componentDidMount() {
    this.timeoutId = setTimeout(
      () => Object.keys(loadableRoutes).forEach(path => loadableRoutes[path].component.preload()),
      5000, // load after 5 sec
    )
  }

  componentWillUnmount() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
  }

  render() {
    return (
      <ConnectedSwitch>
        <Route exact path="/" component={HomePage} />
        {Object.keys(loadableRoutes).map(path => {
          const { exact, ...props } = loadableRoutes[path]
          props.exact = exact === void 0 || exact || false // set true as default
          return <Route key={path} path={path} {...props} />
        })}
        <Route
          render={() => (
            <Page>
              <NotFoundPage />
            </Page>
          )}
        />
      </ConnectedSwitch>
    )
  }
}

export { loadableRoutes }
export default Routes



// WEBPACK FOOTER //
// ./src/routes.js